import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { TicketResultEnum } from "../../../_common/enum/ticketResult.enum";
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { StandardButton } from "../../../_components/button/primary.button";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { caseInvoiceService } from "../../../_services/caseInvoice.service";
import { ticketService } from "../../../_services/ticket.service";

type TicketCloseFollowupDialogProps = {
    open: boolean;
    ticketId?: string;
    ticketType?: string;
    caseId?: string;
    ticketResultId?: number;
    invoiceNumber?: string;
    onClose: () => void;
    onUpdated?: (payload: { ticketResultId?: number; invoiceNumber?: string }) => void;
};

export function TicketCloseFollowupDialog(props: TicketCloseFollowupDialogProps) {
    const [ticketResultId, setTicketResultId] = useState<string | number | null>(props.ticketResultId ?? null);
    const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>(props.invoiceNumber);
    const [savingResult, setSavingResult] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);

    useEffect(() => {
        if (!props.open) return;
        setTicketResultId(props.ticketResultId ?? null);
        setInvoiceNumber(props.invoiceNumber);
    }, [props.open, props.ticketResultId, props.invoiceNumber]);

    const hasResult = !!ticketResultId;
    const hasInvoice = !!invoiceNumber;

    function handleResultChange(event: any) {
        setTicketResultId(event.target.value);
    }

    async function saveTicketResult() {
        if (!props.ticketId || !props.ticketType || !ticketResultId)
            return;

        setSavingResult(true);
        try {
            const ticket = await ticketService.get(props.ticketId, props.ticketType);
            await ticketService.submit({ ...ticket, ticketResultId }, props.ticketType);
            toastr.success("Ticket result updated successfully");
            props.onUpdated?.({ ticketResultId: Number(ticketResultId), invoiceNumber });
        } catch (error) {
            handleErrorResponse(error);
        } finally {
            setSavingResult(false);
        }
    }

    async function generateInvoice() {
        if (!props.caseId || hasInvoice)
            return;

        setGeneratingInvoice(true);
        try {
            await caseInvoiceService.generate({ caseId: props.caseId, invoiceNumber: "" });
            const refreshedTicket = props.ticketId && props.ticketType
                ? await ticketService.get(props.ticketId, props.ticketType)
                : null;

            setInvoiceNumber(refreshedTicket?.invoiceNumber);
            toastr.success("Invoice generated successfully");
            props.onUpdated?.({
                ticketResultId: Number(ticketResultId || refreshedTicket?.ticketResultId || props.ticketResultId || 0) || undefined,
                invoiceNumber: refreshedTicket?.invoiceNumber
            });
        } catch (error) {
            handleErrorResponse(error);
        } finally {
            setGeneratingInvoice(false);
        }
    }

    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Ticket Closed - Final Details</DialogTitle>
            <DialogContent>
                <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body2">
                            This was the last action and the ticket is now closed. You can finalize the ticket result and generate invoice from here.
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <EnumSelect
                            key="ticketResultId"
                            enumObject={TicketResultEnum}
                            label="Select Result"
                            name="ticketResultId"
                            value={ticketResultId}
                            onChange={handleResultChange}
                            fullWidth
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <StandardButton
                            icon="save"
                            type="button"
                            saving={savingResult}
                            loading={savingResult}
                            disabled={!hasResult}
                            onClick={saveTicketResult}
                        >
                            Save Ticket Result
                        </StandardButton>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <StandardButton
                            icon="save"
                            type="button"
                            variant={hasInvoice ? "success" : "primary"}
                            saving={generatingInvoice}
                            loading={generatingInvoice}
                            disabled={hasInvoice || !props.caseId}
                            onClick={generateInvoice}
                        >
                            {hasInvoice ? `Invoice Generated (${invoiceNumber})` : "Generate Invoice"}
                        </StandardButton>
                    </Grid2>
                </Grid2>
            </DialogContent>
            <DialogActions>
                <StandardButton icon="check" type="button" onClick={props.onClose}>
                    Done
                </StandardButton>
            </DialogActions>
        </Dialog>
    );
}
